import React, { useEffect, useMemo, useState } from "react";
import PartnerFinancePage from "../../components/Pricing/PricingPage";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Box,
  InputAdornment,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  addPricingDetails,
  getPricingDetails,
  getRMUser,
  updatePricingDetails,
} from "../../redux/user/userSlice";
import { addBrockerageLogFromFile } from "../../redux/admin/adminSlice";
import { getAIFSchemes } from "../../redux/holdings/holdingSlice";
import toast from "react-hot-toast";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import userService from "../../redux/user/userService";
import AppLoader from "../../components/AppLoader/AppLoader";
import {
  EarningsByProductChart,
  PayoutStatsRow,
  TopInvestorsSection,
} from "../../components/Pricing/PayoutDashboardSections";
import { PayoutDateRangePicker } from "../../components/Pricing/PayoutDateRangePicker";
import {
  primaryButtonSx,
  secondaryButtonSx,
  cardSx,
  BORDER,
  DETAIL_CARD_BG,
} from "../../components/AssetListing/assetListingShared";
import {
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  FILTER_BORDER_DEFAULT,
  INPUT_FOCUS_GLOW,
  OFF_WHITE,
  PROFILE_FONT,
  filterFieldSx,
} from "../../constants/layout";
import {
  LuCheckCircle2,
  LuDownload,
  LuFileText,
  LuFilter,
  LuSearch,
  LuUpload,
  LuX,
} from "react-icons/lu";

const ICON_BLUE = INPUT_BORDER_FOCUS;
const SUCCESS_COLOR = "#08991F";

const emptySchemeFields = {
  schemeName: "",
  isin: "",
  units: "",
  nav: "",
};

const createDefaultFormData = () => ({
  partnerId: "",
  userId: "",
  percentage: "",
  brockergae: "",
  amountToPartner: "",
  paymentDate: new Date().toISOString().split("T")[0],
  holdingType: "",
  comment: "",
  ...emptySchemeFields,
});

const filterSchemeOptions = (schemeNames: any[] | undefined) =>
  (schemeNames || []).filter(
    (scheme: { assetName?: string }) =>
      scheme.assetName && scheme.assetName !== "-"
  );

const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";

const payoutDialogPaperProps = {
  sx: {
    borderRadius: "16px",
    fontFamily: PROFILE_FONT,
    boxShadow: CARD_SHADOW,
    overflow: "hidden",
  },
};

const payoutDialogContentSx = {
  px: { xs: 2, sm: 3 },
  py: 2.5,
};

const payoutModalGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 2,
};

const payoutFieldLabelSx = {
  display: "block",
  fontFamily: PROFILE_FONT,
  fontSize: 14,
  fontWeight: 500,
  color: DARK_NAVY,
  mb: 1,
};

const PayoutModalField = ({
  label,
  children,
  fullWidth = false,
}: {
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) => (
  <Box sx={fullWidth ? { gridColumn: { xs: "1", sm: "1 / -1" } } : undefined}>
    <Typography component="label" sx={payoutFieldLabelSx}>
      {label}
    </Typography>
    {children}
  </Box>
);

const ReadOnlyFieldValue = ({
  value,
  placeholder,
}: {
  value: string;
  placeholder: string;
}) => (
  <Box
    sx={{
      border: `1px solid ${FILTER_BORDER_DEFAULT}`,
      borderRadius: "8px",
      minHeight: 40,
      px: 1.5,
      display: "flex",
      alignItems: "center",
      bgcolor: "#F8FAFC",
    }}
  >
    <Typography
      sx={{
        fontFamily: PROFILE_FONT,
        fontSize: 14,
        color: value ? DARK_NAVY : DARK_NAVY_LIGHT,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value || placeholder}
    </Typography>
  </Box>
);

const SchemeAutocompleteField = ({
  holdingType,
  inputValue,
  options,
  onInputChange,
  onSelect,
  onOpen,
}: {
  holdingType: string | number;
  inputValue: string;
  options: any[];
  onInputChange: (value: string) => void;
  onSelect: (scheme: any) => void;
  onOpen?: () => void;
}) => (
  <Autocomplete
    freeSolo
    options={options}
    filterOptions={(opts) => opts}
    getOptionLabel={(option) =>
      typeof option === "string" ? option : option?.assetName || ""
    }
    isOptionEqualToValue={(option, value) =>
      typeof value === "string"
        ? option?.assetName === value
        : option?.assetName === value?.assetName
    }
    inputValue={inputValue}
    onOpen={onOpen}
    onInputChange={(_, value, reason) => {
      if (reason === "input" || reason === "clear") {
        onInputChange(value);
      }
    }}
    onChange={(_, value) => {
      if (value && typeof value === "object") {
        onSelect(value);
      }
    }}
    disabled={!holdingType}
    componentsProps={{
      popper: { style: { zIndex: 1600 } },
    }}
    renderOption={(props, option) => {
      const isinLabel = option.isin || option.stockSymbol;
      return (
        <li {...props} key={`${option.assetName}-${option.isin || option.id || ""}`}>
          <Box sx={{ display: "flex", flexDirection: "column", py: 0.25 }}>
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY }}>
              {option.assetName}
            </Typography>
            {isinLabel ? (
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 11, color: DARK_NAVY_LIGHT }}>
                {option.isin || option.stockSymbol}
              </Typography>
            ) : null}
          </Box>
        </li>
      );
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        size="small"
        placeholder={
          holdingType ? "Search and select scheme name" : "Select product type first"
        }
        sx={filterFieldSx}
      />
    )}
  />
);

const PayoutDialogTitle = ({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
}) => (
  <DialogTitle
    sx={{
      fontFamily: PROFILE_FONT,
      fontWeight: 700,
      fontSize: 20,
      color: DARK_NAVY,
      pr: 6,
      pb: 1,
    }}
  >
    {title}
    <Typography
      sx={{
        fontFamily: PROFILE_FONT,
        fontSize: 13,
        color: DARK_NAVY_LIGHT,
        fontWeight: 400,
        mt: 0.5,
      }}
    >
      {subtitle}
    </Typography>
    <IconButton
      onClick={onClose}
      aria-label="Close"
      sx={{
        position: "absolute",
        right: 12,
        top: 12,
        color: DARK_NAVY_LIGHT,
        "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
      }}
    >
      <LuX size={20} />
    </IconButton>
  </DialogTitle>
);

const PayoutDialogActions = ({
  onCancel,
  onSubmit,
  submitLabel,
  submitDisabled,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
  submitDisabled?: boolean;
}) => (
  <DialogActions
    sx={{
      px: { xs: 2, sm: 3 },
      py: 2,
      gap: 1,
      justifyContent: "flex-end",
    }}
  >
    <Button
      onClick={onCancel}
      sx={{
        fontFamily: PROFILE_FONT,
        textTransform: "none",
        color: DARK_NAVY_LIGHT,
        fontWeight: 500,
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={onSubmit}
      variant="contained"
      disableElevation
      disabled={submitDisabled}
      sx={{ ...primaryButtonSx, px: 3 }}
    >
      {submitLabel}
    </Button>
  </DialogActions>
);

const Pricing = () => {
  const dispatch = useDispatch<any>();
  const userType = localStorage?.getItem("userType");
  const [open, setOpen] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [sharePercentage, setSharePercentage] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [partners, setPartners] = useState<any[]>([]);
  const [options, setOptions] = useState<any>(-1);
  const [users, setUsers] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(1); // Initial page
  const [pageSize] = useState(1000);
  const [totalPages] = useState(0);
  const [investors, setInvestors] = useState<any[]>([]);
  const [assignedInvestors, setAssignedInvestors] = useState<any[]>([]);
  const [formData, setFormData] = useState(createDefaultFormData);
  const [schemeSearchTerm, setSchemeSearchTerm] = useState("");
  const [schemeOptions, setSchemeOptions] = useState<any[]>([]);
  const [editSchemeSearchTerm, setEditSchemeSearchTerm] = useState("");
  const [editSchemeOptions, setEditSchemeOptions] = useState<any[]>([]);
  const [holdingTypes, setHoldingTypes] = useState<string[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [total, setTotal] = useState<any>(0);
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [selectedDownloadPartner, setSelectedDownloadPartner] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingRow, setEditingRow] = useState<any>(null);
  const [editInvestors, setEditInvestors] = useState<any[]>([]);
  const [editFormData, setEditFormData] = useState({
    id: "",
    partnerId: "",
    userId: "",
    percentage: "",
    brockergae: "",
    paymentDate: new Date().toISOString().split("T")[0],
    holdingType: "",
    comment: "",
    ...emptySchemeFields,
  });

  const [dateRange, setDateRange] = useState<any>({
    startDate: undefined,
    endDate: undefined,
  });

  const normalizePricingUsers = (payload: unknown): any[] => {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === "object" && Array.isArray((payload as { data?: unknown }).data)) {
      return (payload as { data: any[] }).data;
    }
    return [];
  };

  const getPaymentDateTimestamp = (dateValue: unknown): number => {
    if (!dateValue) return 0;
    if (dateValue instanceof Date) return dateValue.getTime();
    if (typeof dateValue === "number") return dateValue;

    if (typeof dateValue === "string") {
      if (dateValue.includes("/")) {
        const [day, month, year] = dateValue.split("/");
        const parsed = new Date(Number(year), Number(month) - 1, Number(day));
        return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
      }

      const parsed = new Date(dateValue);
      return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    }

    return 0;
  };

  const sortByRecentPaymentDate = (rows: any[]) =>
    [...rows].sort(
      (a, b) => getPaymentDateTimestamp(b.paymentDate) - getPaymentDateTimestamp(a.paymentDate)
    );

  const maxChars = 100;

  const handleHoldingTypeChange = (e: any) => {
    const value = e.target.value;
    setHoldingTypes((prev) =>
      prev.includes(value)
        ? prev.filter((type) => type !== value)
        : [...prev, value]
    );
  };

  const fetchPartners = async (pageIndex: number, pageSize: number) => {
    try {
      const response = await dispatch(
        getRMUser({
          userType: 6,
          pageIndex,
          pageSize,
        })
      );
      const data = response.payload.data;
      setPartners(data);
      setCurrentPage(response.payload.pageIndex); // Set current page from response if needed
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };
  const fetchData = async (type?: string) => {
    // if (!partner) return;
    let payload = {
      partnerId: userType === "4" ? options : localStorage.getItem("ID") || options,
      holdingType: type === "all" ? "-1" : holdingTypes.join(",") || "-1", // bond-1,mf-2,fd-3,equity-4,aif-5,pms-6,ppf-7,P.Equiy-8,nps-9
      startDate: type === "all" ? 0 : dateRange?.startDate || 0,
      endDate: type === "all" ? 0 : dateRange?.endDate || 0,
    };

    setLoading(true);
    try {
      const response = await dispatch(getPricingDetails(payload));
      console.log("API Response:", response); // Debugging Log

      const newData = sortByRecentPaymentDate(normalizePricingUsers(response?.payload?.data));
      setUsers(newData);

      if (newData.length > 0) {
        const totalPaidToPartnerSum = newData.reduce(
          (sum, item) => sum + (item?.amountToPartner || 0),
          0
        );
        setTotal(totalPaidToPartnerSum);
      } else {
        setTotal(0);
      }
    } catch (error) {
      console.error("API Error:", error);
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    const loadAssignedInvestors = async () => {
      if (userType !== "6") {
        setAssignedInvestors([]);
        return;
      }

      const partnerId = localStorage.getItem("ID");
      if (!partnerId) return;

      try {
        const res = await userService.getPartnerLogUsers({ partnerId });
        if (res?.data) {
          setAssignedInvestors(Array.isArray(res.data) ? res.data : []);
        }
      } catch (error) {
        console.error("Error fetching assigned investors:", error);
      }
    };

    loadAssignedInvestors();
  }, [userType]);

  useEffect(() => {
    fetchPartners(currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (formData.percentage && formData.brockergae) {
      setFormData({
        ...formData,
        amountToPartner: (
          (Number(formData.percentage) * Number(formData.brockergae)) /
          100
        ).toFixed(2),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.percentage, formData.brockergae]);

  const holdingTypeOptions = [
    { label: "Mutual Fund", value: 2 },
    { label: "Equity", value: 4 },
    { label: "AIF", value: 5 },
    { label: "PMS", value: 6 },
    { label: "Fixed Deposit", value: 3 },
    { label: "Unlisted Equity", value: 8 },
  ];

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "holdingType") {
      setSchemeSearchTerm("");
      setSchemeOptions([]);
      setFormData({
        ...formData,
        holdingType: value,
        ...emptySchemeFields,
      });
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const searchSchemes = async (
    search: string,
    holdingType: string | number,
    setOptions: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    if (!holdingType) {
      setOptions([]);
      return;
    }
    try {
      const response = await dispatch(
        getAIFSchemes({
          pageIndex: 1,
          pageSize: 50,
          search,
          holdingType: Number(holdingType),
        })
      );
      if (response?.payload?.data?.schemeNames || response?.payload?.schemeNames) {
        setOptions(
          filterSchemeOptions(
            response.payload.data?.schemeNames || response.payload.schemeNames
          )
        );
      } else {
        setOptions([]);
      }
    } catch (error) {
      console.error("Error fetching schemes:", error);
      setOptions([]);
    }
  };

  const handleSchemeSearchChange = (value: string) => {
    setSchemeSearchTerm(value);
    setFormData((prev) => ({
      ...prev,
      schemeName: value,
      isin: "",
      nav: "",
    }));
    searchSchemes(value, formData.holdingType, setSchemeOptions);
  };

  const handleSchemeSelect = (scheme: any) => {
    setSchemeSearchTerm(scheme.assetName || "");
    setFormData((prev) => ({
      ...prev,
      schemeName: scheme.assetName || "",
      isin: scheme.isin || scheme.stockSymbol || "",
      nav:
        scheme.currentNAV != null && scheme.currentNAV !== ""
          ? String(scheme.currentNAV)
          : "",
    }));
    setSchemeOptions([]);
  };

  const handleEditSchemeSearchChange = (value: string) => {
    setEditSchemeSearchTerm(value);
    setEditFormData((prev) => ({
      ...prev,
      schemeName: value,
      isin: "",
      nav: "",
    }));
    searchSchemes(value, editFormData.holdingType, setEditSchemeOptions);
  };

  const handleEditSchemeSelect = (scheme: any) => {
    setEditSchemeSearchTerm(scheme.assetName || "");
    setEditFormData((prev) => ({
      ...prev,
      schemeName: scheme.assetName || "",
      isin: scheme.isin || scheme.stockSymbol || "",
      nav:
        scheme.currentNAV != null && scheme.currentNAV !== ""
          ? String(scheme.currentNAV)
          : "",
    }));
    setEditSchemeOptions([]);
  };

  useEffect(() => {
    const fetchInvestors = async () => {
      if (formData.partnerId) {
        try {
          const res = await userService.getPartnerLogUsers({ partnerId: formData.partnerId });
          if (res?.data) {
             setInvestors(res.data);
          }
        } catch (error) {
          console.error(error);
        }
      } else {
        setInvestors([]);
      }
    };
    fetchInvestors();
  }, [formData.partnerId]);

  useEffect(() => {
    const fetchEditInvestors = async () => {
      if (openEditModal && editFormData.partnerId) {
        try {
          const res = await userService.getPartnerLogUsers({
            partnerId: editFormData.partnerId,
          });
          if (res?.data) {
            setEditInvestors(res.data);
          }
        } catch (error) {
          console.error(error);
        }
      } else {
        setEditInvestors([]);
      }
    };
    fetchEditInvestors();
  }, [editFormData.partnerId, openEditModal]);

  const isOptionalPayoutField = (key: string) =>
    key === "comment" ||
    key === "schemeName" ||
    key === "units" ||
    key === "isin" ||
    key === "nav";

  const isFormValid = Object.entries(formData).every(([key, value]) =>
    isOptionalPayoutField(key) ? true : value !== ""
  );

  const formatDate = (dateString: any) => {
    if (!dateString) return ""; // Handle empty value

    if (dateString instanceof Date) {
      // If it's already a Date object, format it properly
      return dateString.toLocaleDateString("en-GB"); // Example format: dd/mm/yyyy
    }

    if (typeof dateString === "string") {
      const [year, month, day] = dateString.split("-");
      return `${day}/${month}/${year}`;
    }

    return ""; // Return empty string if it's an invalid format
  };

  const parsePaymentDateToInput = (dateString: any) => {
    if (!dateString) return new Date().toISOString().split("T")[0];

    if (typeof dateString === "string" && dateString.includes("/")) {
      const [day, month, year] = dateString.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    if (typeof dateString === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    return new Date().toISOString().split("T")[0];
  };

  const resetEditForm = () => {
    setEditFormData({
      id: "",
      partnerId: "",
      userId: "",
      percentage: "",
      brockergae: "",
      paymentDate: new Date().toISOString().split("T")[0],
      holdingType: "",
      comment: "",
      ...emptySchemeFields,
    });
    setEditSchemeSearchTerm("");
    setEditSchemeOptions([]);
    setEditingRow(null);
    setEditInvestors([]);
  };

  const handleEditChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "holdingType") {
      setEditSchemeSearchTerm("");
      setEditSchemeOptions([]);
      setEditFormData({
        ...editFormData,
        holdingType: value,
        ...emptySchemeFields,
      });
      return;
    }
    setEditFormData({ ...editFormData, [name]: value });
  };

  const handleEditClick = (row: any) => {
    setEditingRow(row);
    setEditSchemeSearchTerm(row.schemeName || "");
    setEditSchemeOptions([]);
    setEditFormData({
      id: String(row.id ?? ""),
      partnerId: String(row.partnerId ?? ""),
      userId: row.userId ? String(row.userId) : "",
      percentage: String(Number(row.partnerPercentage || 0) * 100),
      brockergae: String(row.brockerage ?? ""),
      paymentDate: parsePaymentDateToInput(row.paymentDate),
      holdingType: String(row.holdingType ?? ""),
      comment: row.adminComment || "",
      schemeName: row.schemeName || "",
      isin: row.isin || "",
      units: row.units != null && row.units !== "" ? String(row.units) : "",
      nav: row.nav != null && row.nav !== "" ? String(row.nav) : "",
    });
    setOpenEditModal(true);
  };

  const getEditInvestorOptions = () => {
    const options = Array.isArray(editInvestors) ? [...editInvestors] : [];
    const currentUserId = editFormData.userId;

    if (
      currentUserId &&
      !options.some((inv) => String(inv.id) === String(currentUserId))
    ) {
      options.unshift({
        id: Number(currentUserId),
        firstName: editingRow?.firstName || "",
        lastName: editingRow?.lastName || "",
        panCard: editingRow?.panCard || "",
      });
    }

    return options;
  };

  const isEditFormValid = Object.entries(editFormData).every(([key, value]) => {
    if (key === "partnerId" || isOptionalPayoutField(key)) {
      return true;
    }
    return value !== "";
  });

  const handleEditSubmit = async () => {
    const amountToPartner =
      (Number(editFormData.percentage) * Number(editFormData.brockergae)) / 100;

    const { isin, units, nav } = payoutFieldValues({
      isin: editFormData.isin,
      units: editFormData.units,
      nav: editFormData.nav,
    });

    const payload = {
      id: Number(editFormData.id),
      userId: Number(editFormData.userId) || null,
      partnerId: Number(editFormData.partnerId),
      percentage: Number(editFormData.percentage) / 100,
      brockergae: Number(editFormData.brockergae),
      amountToPartner,
      paymentDate: formatDate(editFormData.paymentDate),
      holdingType: editFormData.holdingType,
      comment: editFormData.comment,
      schemeName: editFormData.schemeName || null,
      isin,
      units,
      nav,
    };

    try {
      const response = await dispatch(updatePricingDetails(payload));
      if (response?.payload?.statusCode === 200) {
        toast.success("Payout updated successfully.");
        fetchData();
      } else {
        toast.error("Payout not updated. Try again later.");
      }
    } catch (error) {
      console.error(error, "Update payout");
      toast.error("Something went wrong. Try again later.");
    } finally {
      setOpenEditModal(false);
      resetEditForm();
    }
  };

  const payoutFieldValues = (fields: { isin: string; units: string; nav: string }) => ({
    isin: fields.isin || null,
    units: fields.units !== "" ? Number(fields.units) : null,
    nav: fields.nav !== "" ? Number(fields.nav) : null,
  });

  const handleSubmit = async () => {
    const { isin, units, nav } = payoutFieldValues({
      isin: formData.isin,
      units: formData.units,
      nav: formData.nav,
    });
    let payload = {
      userId: Number(formData?.userId) || null,
      partnerId: Number(formData?.partnerId),
      percentage: Number(formData?.percentage) / 100,
      brockergae: Number(formData?.brockergae),
      amountToPartner: Number(formData?.amountToPartner),
      paymentDate: formatDate(formData?.paymentDate),
      holdingType: formData?.holdingType,
      comment: formData?.comment,
      schemeName: formData.schemeName || null,
      isin,
      units,
      nav,
    };
    try {
      const response = await dispatch(addPricingDetails(payload));
      if (response?.payload?.statusCode === 200) {
        toast.success("Pricing added to the partner successfully.");
        fetchData();
      } else {
        toast.error("Pricing not added.Try after sometimes.");
      }
    } catch (error) {
      console.error(error, "Pricing");
      toast.error("Somethings went wrong.Try after sometimes.");
    } finally {
      setOpen(false);
      setSchemeSearchTerm("");
      setSchemeOptions([]);
      setFormData(createDefaultFormData());
    }
  };

  // const handleHoldingTypeChangeNew = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const selectedType = Number(event.target.value); // Convert to number
  //   setHoldingType(selectedType);

  //   // Filter users based on selected holding type
  //   if (selectedType === 0) {
  //     setFilteredUsers(users);
  //   } else {
  //     const filteredData = Array.isArray(users) ? users.filter((user: any) => user.holdingType === selectedType) : [];
  //     setFilteredUsers(filteredData);
  //   }
  // };

  // Update filteredUsers whenever users change
  useEffect(() => {
    setFilteredUsers(Array.isArray(users) ? users : []);
  }, [users]);

  const resetFilters = () => {
    setHoldingTypes([]);
    setDateRange({
      startDate: undefined,
      endDate: undefined,
    });
    setOptions(-1);
    fetchData("all");
  };

  const handleApplyFilters = () => {
    fetchData();
    setFilterModalOpen(false);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (userType === "4" && options !== -1) count += 1;
    if (holdingTypes.length > 0) count += 1;
    if (dateRange?.startDate && dateRange?.endDate) count += 1;
    return count;
  }, [userType, options, holdingTypes, dateRange]);

  const partnerOrInvestorCount = useMemo(() => {
    if (userType === "4") return partners?.length || 0;
    return assignedInvestors.length;
  }, [userType, partners, assignedInvestors]);

  const handleUploadSubmit = async () => {
    if (!uploadedFile || !sharePercentage) {
      toast.error("Please upload a file and enter share percentage");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("percentage", sharePercentage);

      const result = await dispatch(addBrockerageLogFromFile(formData));
      
      if (result.payload.statusCode === 200) {
        toast.success("File uploaded and processed successfully!");
        handleCloseUploadModal();
        // Refresh the data
        fetchData(options);
      } else {
        toast.error(result.error?.message || "Upload failed");
      }
    } catch (error: any) {
      toast.error(error?.message || "An error occurred during upload");
    }
  };

  const validateCsvFile = (file: File) => {
    const validTypes = ["text/csv", "application/csv"];
    if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a valid CSV file (.csv)");
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return false;
    }
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && validateCsvFile(file)) {
      setUploadedFile(file);
      toast.success("File uploaded successfully!");
    }
  };

  const handleCloseUploadModal = () => {
    setOpenUploadModal(false);
    setUploadedFile(null);
    setSharePercentage("");
    setIsDragging(false);
  };

  const handleDownloadCSV = () => {
    const groupedData = users.reduce((acc: any, curr: any) => {
      const uniqueKey = curr.panCard || `${curr.firstName}-${curr.lastName}`;

      if (!acc[uniqueKey]) {
        acc[uniqueKey] = {
          "Investor Name": `${curr.firstName || ""} ${curr.lastName || ""}`.trim(),
          "PAN Card": curr.panCard || "N/A",
          "Mutual Funds (\u20b9)": 0,
          "Equity (\u20b9)": 0,
          "AIF (\u20b9)": 0,
          "PMS (\u20b9)": 0,
          "Fixed Deposit (\u20b9)": 0,
          "Unlisted Equity (\u20b9)": 0,
          "Total (\u20b9)": 0,
        };
      }

      const amount = Number(curr.amountToPartner) || 0;
      acc[uniqueKey]["Total (\u20b9)"] += amount;

      switch (curr.holdingType) {
        case 2: acc[uniqueKey]["Mutual Funds (\u20b9)"] += amount; break;
        case 3: acc[uniqueKey]["Fixed Deposit (\u20b9)"] += amount; break;
        case 4: acc[uniqueKey]["Equity (\u20b9)"] += amount; break;
        case 5: acc[uniqueKey]["AIF (\u20b9)"] += amount; break;
        case 6: acc[uniqueKey]["PMS (\u20b9)"] += amount; break;
        case 8: acc[uniqueKey]["Unlisted Equity (\u20b9)"] += amount; break;
        default: break;
      }

      return acc;
    }, {});

    const rows = Object.values(groupedData) as any[];
    if (rows.length === 0) return;

    const headers = [
      "Investor Name",
      "PAN Card",
      "Mutual Funds (\u20b9)",
      "Equity (\u20b9)",
      "AIF (\u20b9)",
      "PMS (\u20b9)",
      "Fixed Deposit (\u20b9)",
      "Unlisted Equity (\u20b9)",
      "Total (\u20b9)"
    ];

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        headers.map((header) => {
          let value = row[header];
          return typeof value === "string" ? `"${value}"` : value.toFixed(2);
        }).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.setAttribute("download", `Partner_Payout_Report_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleAdminDownloadCSV = () => {
    if (!selectedDownloadPartner) return;

    const partnerData = users.filter(
      (u: any) => String(u.partnerId) === String(selectedDownloadPartner)
    );

    const partnerObj = partners.find((p: any) => String(p.id) === String(selectedDownloadPartner));
    const partnerName = partnerObj ? partnerObj.firstName : `Partner_${selectedDownloadPartner}`;

    const groupedData = partnerData.reduce((acc: any, curr: any) => {
      const uniqueKey = curr.panCard || `${curr.firstName}-${curr.lastName}`;

      if (!acc[uniqueKey]) {
        acc[uniqueKey] = {
          "Investor Name": `${curr.firstName || ""} ${curr.lastName || ""}`.trim(),
          "PAN Card": curr.panCard || "N/A",
          "Mutual Funds (\u20b9)": 0,
          "Equity (\u20b9)": 0,
          "AIF (\u20b9)": 0,
          "PMS (\u20b9)": 0,
          "Fixed Deposit (\u20b9)": 0,
          "Unlisted Equity (\u20b9)": 0,
          "Total (\u20b9)": 0,
        };
      }

      const amount = Number(curr.amountToPartner) || 0;
      acc[uniqueKey]["Total (\u20b9)"] += amount;

      switch (curr.holdingType) {
        case 2: acc[uniqueKey]["Mutual Funds (\u20b9)"] += amount; break;
        case 3: acc[uniqueKey]["Fixed Deposit (\u20b9)"] += amount; break;
        case 4: acc[uniqueKey]["Equity (\u20b9)"] += amount; break;
        case 5: acc[uniqueKey]["AIF (\u20b9)"] += amount; break;
        case 6: acc[uniqueKey]["PMS (\u20b9)"] += amount; break;
        case 8: acc[uniqueKey]["Unlisted Equity (\u20b9)"] += amount; break;
        default: break;
      }

      return acc;
    }, {});

    const rows = Object.values(groupedData) as any[];

    if (rows.length === 0) {
      toast.error(`No payout data found for ${partnerName}.`);
      return;
    }

    const headers = [
      "Investor Name",
      "PAN Card",
      "Mutual Funds (\u20b9)",
      "Equity (\u20b9)",
      "AIF (\u20b9)",
      "PMS (\u20b9)",
      "Fixed Deposit (\u20b9)",
      "Unlisted Equity (\u20b9)",
      "Total (\u20b9)"
    ];

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        headers.map((header) => {
          let value = row[header];
          return typeof value === "string" ? `"${value}"` : value.toFixed(2);
        }).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.setAttribute("download", `Payout_Report_${partnerName}_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    setOpenDownloadDialog(false);
    setSelectedDownloadPartner("");
  };

  const handleDownloadMFDetailedCSV = () => {
    if (users.length === 0) return;

    const headers = [
      "Date",
      "Investor Name",
      "Scheme Name",
      "ISIN",
      "Units",
      "NAV",
      "Total Brokerage (Rs.)",
      "Brokerage Shared (Rs.)"
    ];

    const csvContent = [
      headers.join(","),
      ...users.map((row: any) => [
        row.paymentDate || "N/A",
        `"${row.firstName ? `${row.firstName} ${row.lastName || ""}`.trim() : "N/A"}"`,
        `"${row.schemeName || row.adminComment || "N/A"}"`,
        row.isin || "N/A",
        row.units || 0,
        row.nav || 0,
        row.brockerage || 0,
        row.amountToPartner || 0
      ].join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.setAttribute("download", `MF_Brokerage_Report_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: OFF_WHITE,
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        py: { xs: 3, md: 4 },
        fontFamily: PROFILE_FONT,
      }}
    >
      <AppLoader loading={loading} />

      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: { xs: 24, md: 28 },
                fontWeight: 700,
                color: DARK_NAVY,
                mb: 0.5,
              }}
            >
              {userType === "4" ? "Partner Payout" : "Payout Details"}
            </Typography>
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
              Track earnings and payouts across investors and products.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
            {userType === "4" && (
              <>
                <Button
                  onClick={() => setOpenUploadModal(true)}
                  startIcon={<LuUpload size={18} />}
                  sx={secondaryButtonSx}
                >
                  Upload
                </Button>
                <Button
                  onClick={() => {
                    setSelectedDownloadPartner("");
                    setOpenDownloadDialog(true);
                  }}
                  startIcon={<LuDownload size={18} />}
                  sx={primaryButtonSx}
                >
                  Download Report
                </Button>
                <Button onClick={() => setOpen(true)} sx={primaryButtonSx}>
                  Add Partner Payout
                </Button>
              </>
            )}
            {userType === "6" && users.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <Button
                  onClick={handleDownloadMFDetailedCSV}
                  startIcon={<LuFileText size={18} />}
                  sx={primaryButtonSx}
                >
                  Brokerage Report
                </Button>
                <Button
                  onClick={handleDownloadCSV}
                  startIcon={<LuDownload size={18} />}
                  sx={primaryButtonSx}
                >
                  Download Report
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {(userType === "4" || userType === "6") && (
          <>
            <PayoutStatsRow
              totalEarnings={total}
              totalPartners={partnerOrInvestorCount}
              totalTransactions={Array.isArray(users) ? users.length : 0}
              isAdmin={userType === "4"}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                gap: 2.5,
                mb: 2.5,
              }}
            >
              <TopInvestorsSection users={users} />
              <EarningsByProductChart users={users} />
            </Box>

            <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  alignItems: { xs: "stretch", sm: "center" },
                }}
              >
                <TextField
                  placeholder="Search investor or product..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  variant="outlined"
                  fullWidth
                  sx={{ ...filterFieldSx, flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          pl: 0.5,
                          pr: 1,
                          color: DARK_NAVY_LIGHT,
                        }}
                      >
                        <LuSearch size={18} />
                      </Box>
                    ),
                  }}
                />

                <Button
                  onClick={() => setFilterModalOpen(true)}
                  startIcon={<LuFilter size={16} />}
                  sx={{ ...secondaryButtonSx, whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                </Button>
              </Box>
            </Box>
          </>
        )}

        <PartnerFinancePage
          users={filteredUsers}
          onChange={handleChange}
          totalPages={totalPages}
          currentPage={currentPage}
          pageSize={pageSize}
          isAdmin={userType === "4"}
          onEdit={handleEditClick}
          searchQuery={searchQuery}
        />
      </Box>

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            fontFamily: PROFILE_FONT,
            boxShadow: CARD_SHADOW,
            overflow: "hidden",
            maxWidth: { xs: "100%", sm: 720, md: 860 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: PROFILE_FONT,
            fontWeight: 700,
            fontSize: 20,
            color: DARK_NAVY,
            pr: 6,
            pb: 1,
          }}
        >
          Filters
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: 13,
              color: DARK_NAVY_LIGHT,
              fontWeight: 400,
              mt: 0.5,
            }}
          >
            Refine payout data by partner, product type, and date range.
          </Typography>
          <IconButton
            onClick={() => setFilterModalOpen(false)}
            aria-label="Close filters"
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: DARK_NAVY_LIGHT,
              "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
            }}
          >
            <LuX size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2.5,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {userType === "4" && (
              <FormControl fullWidth size="small" sx={filterFieldSx}>
                <InputLabel id="filter-partner-label">Partner</InputLabel>
                <Select
                  labelId="filter-partner-label"
                  value={options}
                  label="Partner"
                  onChange={(e: any) => setOptions(e.target.value)}
                >
                  <MenuItem value={-1}>All Partners</MenuItem>
                  {Array.isArray(partners) &&
                    partners.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.firstName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            <Box>
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 14,
                  fontWeight: 600,
                  color: DARK_NAVY,
                  mb: 1.25,
                }}
              >
                Holding Type
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  columnGap: 2.5,
                  rowGap: 1.25,
                }}
              >
                {holdingTypeOptions.map((option) => {
                  const value = option.value.toString();
                  const checked = holdingTypes.includes(value);
                  return (
                    <Box
                      key={option.value}
                      onClick={() =>
                        handleHoldingTypeChange({
                          target: { value },
                        })
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        cursor: "pointer",
                        py: 0.25,
                        flex: "0 0 calc(33.333% - 1.667rem)",
                        minWidth: 130,
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: "4px",
                          border: `2px solid ${checked ? ICON_BLUE : "#C5C5C5"}`,
                          bgcolor: checked ? ICON_BLUE : "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {checked ? (
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 5,
                              borderLeft: "2px solid #fff",
                              borderBottom: "2px solid #fff",
                              transform: "rotate(-45deg) translateY(-1px)",
                            }}
                          />
                        ) : null}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: PROFILE_FONT,
                          fontSize: 14,
                          color: checked ? ICON_BLUE : DARK_NAVY,
                          fontWeight: checked ? 600 : 400,
                        }}
                      >
                        {option.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <PayoutDateRangePicker
              startDate={dateRange?.startDate}
              endDate={dateRange?.endDate}
              onChange={setDateRange}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => {
              resetFilters();
              setFilterModalOpen(false);
            }}
            sx={{
              fontFamily: PROFILE_FONT,
              textTransform: "none",
              color: DARK_NAVY_LIGHT,
              fontWeight: 500,
            }}
          >
            Reset
          </Button>
          <Button onClick={handleApplyFilters} sx={{ ...primaryButtonSx, px: 3 }}>
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={payoutDialogPaperProps}
      >
        <PayoutDialogTitle
          title="Add Partner Payout"
          subtitle="Record a new payout entry for a partner."
          onClose={() => setOpen(false)}
        />
        <DialogContent dividers sx={payoutDialogContentSx}>
          <Box sx={payoutModalGridSx}>
            <PayoutModalField label="Partner ID">
              <TextField
                select
                name="partnerId"
                fullWidth
                size="small"
                value={formData.partnerId}
                onChange={handleChange}
                sx={filterFieldSx}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  Select Partner
                </MenuItem>
                {Array.isArray(partners) &&
                  partners.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.firstName}
                    </MenuItem>
                  ))}
              </TextField>
            </PayoutModalField>

            <PayoutModalField label="Product Type">
              <TextField
                select
                name="holdingType"
                fullWidth
                size="small"
                value={formData.holdingType}
                onChange={handleChange}
                sx={filterFieldSx}
              >
                {holdingTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </PayoutModalField>

            <PayoutModalField label="Partner Share (%)">
              <TextField
                type="number"
                placeholder="Enter percentage"
                name="percentage"
                fullWidth
                size="small"
                value={formData.percentage}
                onChange={handleChange}
                sx={filterFieldSx}
              />
            </PayoutModalField>

            <PayoutModalField label="Brokerage Amount">
              <TextField
                type="number"
                placeholder="Enter brokerage amount"
                name="brockergae"
                fullWidth
                size="small"
                value={formData.brockergae}
                onChange={handleChange}
                sx={filterFieldSx}
              />
            </PayoutModalField>

            <PayoutModalField label="Date">
              <TextField
                type="date"
                name="paymentDate"
                fullWidth
                size="small"
                value={formData.paymentDate}
                onChange={handleChange}
                sx={filterFieldSx}
              />
            </PayoutModalField>

            <PayoutModalField label="Investor">
              <TextField
                select
                name="userId"
                fullWidth
                size="small"
                value={formData.userId}
                onChange={handleChange}
                sx={filterFieldSx}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  Select investor
                </MenuItem>
                {Array.isArray(investors) &&
                  investors.map((inv) => (
                    <MenuItem key={inv.id} value={inv.id}>
                      {inv.firstName} {inv.lastName}{" "}
                      {inv.panCard ? `(${inv.panCard})` : ""}
                    </MenuItem>
                  ))}
              </TextField>
            </PayoutModalField>

            <PayoutModalField label="Scheme Name" fullWidth>
              <SchemeAutocompleteField
                holdingType={formData.holdingType}
                inputValue={schemeSearchTerm}
                options={schemeOptions}
                onInputChange={handleSchemeSearchChange}
                onSelect={handleSchemeSelect}
                onOpen={() => {
                  if (formData.holdingType) {
                    searchSchemes(schemeSearchTerm, formData.holdingType, setSchemeOptions);
                  }
                }}
              />
            </PayoutModalField>

            <PayoutModalField label="ISIN">
              <ReadOnlyFieldValue
                value={formData.isin}
                placeholder="Auto-filled from scheme"
              />
            </PayoutModalField>

            <PayoutModalField label="NAV">
              <TextField
                type="number"
                name="nav"
                fullWidth
                size="small"
                placeholder="Enter NAV"
                value={formData.nav}
                onChange={handleChange}
                sx={filterFieldSx}
              />
            </PayoutModalField>

            <PayoutModalField label="Units">
              <TextField
                type="number"
                name="units"
                fullWidth
                size="small"
                placeholder="Enter units"
                value={formData.units}
                onChange={handleChange}
                sx={filterFieldSx}
              />
            </PayoutModalField>

            <PayoutModalField label="Details (amount, asset name, comments)" fullWidth>
              <TextField
                name="comment"
                multiline
                fullWidth
                rows={3}
                size="small"
                placeholder="Enter payout details"
                value={formData.comment}
                onChange={(e) => {
                  if (formData.comment.length >= maxChars) {
                    return;
                  }
                  handleChange(e);
                }}
                sx={filterFieldSx}
              />
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 12,
                  color:
                    formData.comment.length >= maxChars ? "#d32f2f" : DARK_NAVY_LIGHT,
                  mt: 0.75,
                }}
              >
                {formData.comment.length} / {maxChars} characters
              </Typography>
            </PayoutModalField>
          </Box>
        </DialogContent>
        <PayoutDialogActions
          onCancel={() => setOpen(false)}
          onSubmit={handleSubmit}
          submitLabel="Submit"
          submitDisabled={!isFormValid}
        />
      </Dialog>

      {/* Edit Modal */}
      <Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          resetEditForm();
        }}
        fullWidth
        maxWidth="md"
        PaperProps={payoutDialogPaperProps}
      >
        <PayoutDialogTitle
          title="Edit Partner Payout"
          subtitle="Update payout details for this entry."
          onClose={() => {
            setOpenEditModal(false);
            resetEditForm();
          }}
        />
        <DialogContent dividers sx={payoutDialogContentSx}>
          <Box sx={payoutModalGridSx}>
            <PayoutModalField label="Product Type">
              <TextField
                select
                name="holdingType"
                fullWidth
                size="small"
                value={editFormData.holdingType}
                onChange={handleEditChange}
                sx={filterFieldSx}
              >
                {holdingTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </PayoutModalField>

            <PayoutModalField label="Date">
              <TextField
                type="date"
                name="paymentDate"
                fullWidth
                size="small"
                value={editFormData.paymentDate}
                onChange={handleEditChange}
                sx={filterFieldSx}
              />
            </PayoutModalField>

            <PayoutModalField label="Investor" fullWidth>
              <TextField
                select
                name="userId"
                fullWidth
                size="small"
                value={editFormData.userId}
                onChange={handleEditChange}
                sx={filterFieldSx}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  Select investor
                </MenuItem>
                {getEditInvestorOptions().map((inv) => (
                  <MenuItem key={inv.id} value={inv.id}>
                    {inv.firstName} {inv.lastName}{" "}
                    {inv.panCard ? `(${inv.panCard})` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </PayoutModalField>

            <PayoutModalField label="Scheme Name" fullWidth>
              <SchemeAutocompleteField
                holdingType={editFormData.holdingType}
                inputValue={editSchemeSearchTerm}
                options={editSchemeOptions}
                onInputChange={handleEditSchemeSearchChange}
                onSelect={handleEditSchemeSelect}
                onOpen={() => {
                  if (editFormData.holdingType) {
                    searchSchemes(
                      editSchemeSearchTerm,
                      editFormData.holdingType,
                      setEditSchemeOptions
                    );
                  }
                }}
              />
            </PayoutModalField>

            <PayoutModalField label="ISIN">
              <ReadOnlyFieldValue
                value={editFormData.isin}
                placeholder="Auto-filled from scheme"
              />
            </PayoutModalField>

            <PayoutModalField label="NAV">
              <TextField
                type="number"
                name="nav"
                fullWidth
                size="small"
                placeholder="Enter NAV"
                value={editFormData.nav}
                onChange={handleEditChange}
                sx={filterFieldSx}
              />
            </PayoutModalField>

            <PayoutModalField label="Units">
              <TextField
                type="number"
                name="units"
                fullWidth
                size="small"
                placeholder="Enter units"
                value={editFormData.units}
                onChange={handleEditChange}
                sx={filterFieldSx}
              />
            </PayoutModalField>

            <PayoutModalField label="Brokerage Amount (Rs)">
              <TextField
                type="number"
                placeholder="Enter brokerage amount"
                name="brockergae"
                fullWidth
                size="small"
                value={editFormData.brockergae}
                onChange={handleEditChange}
                sx={filterFieldSx}
              />
            </PayoutModalField>

            <PayoutModalField label="Partner Share (%)">
              <TextField
                type="number"
                placeholder="Enter percentage"
                name="percentage"
                fullWidth
                size="small"
                value={editFormData.percentage}
                onChange={handleEditChange}
                sx={filterFieldSx}
              />
            </PayoutModalField>

            <PayoutModalField label="Comment" fullWidth>
              <TextField
                name="comment"
                multiline
                fullWidth
                rows={3}
                size="small"
                placeholder="Enter comment"
                value={editFormData.comment}
                onChange={(e) => {
                  if (editFormData.comment.length >= maxChars) {
                    return;
                  }
                  handleEditChange(e);
                }}
                sx={filterFieldSx}
              />
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 12,
                  color:
                    editFormData.comment.length >= maxChars ? "#d32f2f" : DARK_NAVY_LIGHT,
                  mt: 0.75,
                }}
              >
                {editFormData.comment.length} / {maxChars} characters
              </Typography>
            </PayoutModalField>
          </Box>
        </DialogContent>
        <PayoutDialogActions
          onCancel={() => {
            setOpenEditModal(false);
            resetEditForm();
          }}
          onSubmit={handleEditSubmit}
          submitLabel="Save"
          submitDisabled={!isEditFormValid}
        />
      </Dialog>

      {/* Upload Modal */}
      <Dialog
        open={openUploadModal}
        onClose={handleCloseUploadModal}
        maxWidth="sm"
        fullWidth
        PaperProps={payoutDialogPaperProps}
      >
        <PayoutDialogTitle
          title="Upload Mutual Fund Data"
          subtitle="Upload a CSV file with share percentage to process partner payouts."
          onClose={handleCloseUploadModal}
        />
        <DialogContent dividers sx={payoutDialogContentSx}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <PayoutModalField label="Share Percentage (%)">
              <TextField
                name="sharePercentage"
                fullWidth
                size="small"
                type="number"
                value={sharePercentage}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || (Number(value) >= 0 && Number(value) <= 100)) {
                    setSharePercentage(value);
                  }
                }}
                placeholder="Enter percentage (0–100)"
                sx={filterFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        sx={{
                          fontFamily: PROFILE_FONT,
                          fontSize: 14,
                          color: DARK_NAVY_LIGHT,
                          fontWeight: 600,
                        }}
                      >
                        %
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 12,
                  color: DARK_NAVY_LIGHT,
                  mt: 0.75,
                }}
              >
                Enter the share percentage for mutual fund pricing
              </Typography>
            </PayoutModalField>

            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files?.[0];
                if (file && validateCsvFile(file)) {
                  setUploadedFile(file);
                  toast.success("File uploaded successfully!");
                }
              }}
              sx={{
                border: `2px dashed ${
                  isDragging ? ICON_BLUE : uploadedFile ? SUCCESS_COLOR : FILTER_BORDER_DEFAULT
                }`,
                borderRadius: "12px",
                p: 4,
                textAlign: "center",
                bgcolor: isDragging ? OFF_WHITE : uploadedFile ? DETAIL_CARD_BG : "#FAFAFA",
                cursor: "pointer",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
                "&:hover": {
                  borderColor: uploadedFile ? SUCCESS_COLOR : ICON_BLUE,
                  bgcolor: DETAIL_CARD_BG,
                },
                "&:focus-within": {
                  borderColor: ICON_BLUE,
                  boxShadow: INPUT_FOCUS_GLOW,
                },
              }}
            >
              <input
                type="file"
                accept=".csv"
                hidden
                id="csv-file-upload"
                onChange={handleFileChange}
              />
              <label htmlFor="csv-file-upload" style={{ cursor: "pointer", display: "block" }}>
                {uploadedFile ? (
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <LuCheckCircle2 size={48} color={SUCCESS_COLOR} />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 16,
                        color: SUCCESS_COLOR,
                        mb: 1,
                        fontWeight: 600,
                      }}
                    >
                      File Uploaded Successfully!
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <LuFileText size={18} color={DARK_NAVY_LIGHT} />
                      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
                        {uploadedFile.name}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT }}>
                      Size: {(uploadedFile.size / 1024).toFixed(2)} KB
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 14,
                        color: ICON_BLUE,
                        mt: 2,
                        fontWeight: 500,
                      }}
                    >
                      Click or drag to replace
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <LuUpload size={56} color={isDragging ? ICON_BLUE : DARK_NAVY_LIGHT} />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 16,
                        mb: 1,
                        fontWeight: 600,
                        color: DARK_NAVY,
                      }}
                    >
                      {isDragging ? "Drop your CSV file here" : "Drag & Drop your CSV file here"}
                    </Typography>
                    <Typography
                      sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT, mb: 2 }}
                    >
                      or click to browse
                    </Typography>
                    <Box sx={{ ...primaryButtonSx, display: "inline-block", px: 3, py: 1 }}>
                      Choose File
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 12,
                        display: "block",
                        mt: 2,
                        color: DARK_NAVY_LIGHT,
                      }}
                    >
                      Accepted format: .csv • Maximum file size: 10MB
                    </Typography>
                  </Box>
                )}
              </label>
            </Box>

            <Box
              sx={{
                p: 2,
                bgcolor: OFF_WHITE,
                borderRadius: "10px",
                border: `1px solid ${BORDER}`,
              }}
            >
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT }}>
                Upload mutual fund data in CSV format with share percentage to process partner
                payouts.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <PayoutDialogActions
          onCancel={handleCloseUploadModal}
          onSubmit={handleUploadSubmit}
          submitLabel="Upload & Process"
          submitDisabled={!uploadedFile || !sharePercentage}
        />
      </Dialog>

      {/* Admin - Download Report: Partner Selection Dialog */}
      <Dialog
        open={openDownloadDialog}
        onClose={() => setOpenDownloadDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Download Partner Payout Report
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setOpenDownloadDialog(false)}
            aria-label="close"
            sx={{ position: "absolute", right: 24, top: 8, color: "gray" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Select a partner to download their payout report as a CSV file.
          </Typography>
          <TextField
            select
            label="Select Partner"
            fullWidth
            margin="dense"
            value={selectedDownloadPartner}
            onChange={(e) => setSelectedDownloadPartner(e.target.value)}
            sx={{
              "& .MuiInputBase-input": {
                padding: "10px 12px",
                fontSize: "1rem",
              },
            }}
          >
            <MenuItem value="" disabled>
              Choose a partner
            </MenuItem>
            {Array.isArray(partners) &&
              partners.map((p: any) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.firstName} {p.lastName || ""} ({p.aumUserId})
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setOpenDownloadDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            disabled={!selectedDownloadPartner}
            onClick={handleAdminDownloadCSV}
          >
            Download CSV
          </Button>
        </DialogActions>
      </Dialog>
    </Box>

  );
};

export default Pricing;
